import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import timezone from "dayjs/plugin/timezone"
import { ref, set, update, get } from "firebase/database"

import { sdk9db } from "../firebase"
import { DEFAULT_TIMEZONE } from "../config"
import { StudentObj } from "../types"
import { formatDate } from "./dateUtils"
import { sendSlackMessage } from "./slackUtils"

dayjs.extend(utc)
dayjs.extend(timezone)

type ReviewPre = {
  text: string
  rating: number
  date: string
}

const abbreviateName = (name: string) => {
  const words = name.trim().split(" ")
  if (words.length === 1) {
    return words[0]
  }
  const first = words[0]
  const last = words[words.length - 1]
  return `${first} ${last[0]}.`
}

const makeReviewObj = (
  review: ReviewPre,
  student: StudentObj,
  source: string = "",
) => ({
  studentId: student.id,
  studentName: abbreviateName(student.name),
  date: dayjs.tz(review.date, DEFAULT_TIMEZONE).valueOf(),
  rating: review.rating,
  text: review.text,
  source,
})

export const saveReview = async (
  instructorId: string,
  lessonId: string,
  student: StudentObj,
  review: ReviewPre,
  source: string,
) => {
  const studentReview = makeReviewObj(review, student, source)

  if (review.rating < 4 || review.text.length > 30) {
    const instructorName = await get(
      ref(sdk9db, `instructors/${instructorId}/displayName`),
    ).then((snap) => snap.val())

    const lesson = await get(
      ref(sdk9db, `studentLessonSlotsV2/${student.id}/${lessonId}`),
    ).then((snap) => snap.val())

    let message = `New instructor review for ${instructorName} from ${student.name} `
    message += `for lesson on ${formatDate(lesson.details.startTime, lesson.instructor.timeZone || DEFAULT_TIMEZONE, "long")}\n\n`
    if (review.text) {
      message += `${review.text}\n`
    }
    message += `${review.rating}/5 stars`

    const channel =
      review.rating < 4
        ? "#support-reviews-3-or-less"
        : "#support-reviews-4-plus"
    await sendSlackMessage(channel, ":star:", message)
  }
  return set(
    ref(
      sdk9db,
      `instructorReviewsV3/${instructorId}/${lessonId}/${student.id}`,
    ),
    studentReview,
  )
}

export const updateReview = (
  instructorId: string,
  lessonId: string,
  student: StudentObj,
  review: ReviewPre,
) => {
  const studentReview = makeReviewObj(review, student)
  return update(
    ref(
      sdk9db,
      `instructorReviewsV3/${instructorId}/${lessonId}/${student.id}`,
    ),
    studentReview,
  )
}

export const getReview = (
  instructorId: string,
  lessonId: string,
  studentId: string,
) =>
  get(
    ref(sdk9db, `instructorReviewsV3/${instructorId}/${lessonId}/${studentId}`),
  ).then((snap) => snap.val())
